import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SocialSection = styled.section`
  padding: 120px 0 80px;
  background: linear-gradient(180deg, #fff 0%, #f5f5f5 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 90% 10%, rgba(212, 175, 55, 0.05), rgba(0,0,0,0) 50%),
      linear-gradient(to bottom right, rgba(212, 175, 55, 0.03), rgba(0,0,0,0) 70%);
    pointer-events: none;
  }
`;

const SocialContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SocialHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: #111;
  margin-bottom: 20px;
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    display: none;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: #444;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SocialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SocialCard = styled(motion.a)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 50px 30px;
  background: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(212, 175, 55, 0.1);
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: rgba(212, 175, 55, 0.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${props => props.className === 'instagram' 
      ? 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
      : props.className === 'linkedin' 
      ? '#0077B5' 
      : '#FF0000'};
    transition: height 0.3s ease;
  }
  
  &:hover::after {
    height: 7px;
  }
`;

const SocialIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  font-size: 2rem;
  color: white;
  overflow: hidden;
  
  &.instagram {
    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
  }
  
  &.linkedin {
    background: #0077B5;
  }
  
  &.youtube {
    background: #FF0000;
  }
  
  svg {
    width: 40px;
    height: 40px;
    fill: white;
  }
`;

const SocialName = styled.h3`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
  font-weight: 500;
`;

const SocialHandle = styled.p`
  font-size: 1.1rem;
  color: #D4AF37;
  margin-bottom: 20px;
  font-weight: 500;
`;

const SocialDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
`;

const ConnectButton = styled.div`
  display: inline-block;
  margin-top: 25px;
  padding: 10px 25px;
  background-color: #000;
  color: #fff;
  border-radius: 50px;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  
  ${SocialCard}:hover & {
    background-color: ${props => props.className === 'instagram' 
      ? '#E1306C' 
      : props.className === 'linkedin' 
      ? '#0077B5' 
      : '#FF0000'};
    transform: translateY(-3px);
  }
`;

const Social: React.FC = () => {
  // Social media profiles data
  const socialProfiles = [
    {
      name: 'Instagram',
      handle: '@barkinsayin_official',
      icon: 'instagram',
      url: 'https://www.instagram.com/barkinsayin_official/',
      description: 'Follow for daily insights, lifestyle updates, and behind-the-scenes moments from my entrepreneurial journey.',
    },
    {
      name: 'LinkedIn',
      handle: 'Barkın Sayın',
      icon: 'linkedin',
      url: 'https://www.linkedin.com/in/barkinsayin/',
      description: 'Connect with me for professional networking, business opportunities, and thought leadership in the business space.',
    },
    {
      name: 'YouTube',
      handle: 'Barkın Sayın Official',
      icon: 'youtube',
      url: 'https://www.youtube.com/@BarkinSayin',
      description: 'Subscribe for video interviews, business insights, and expert discussions on entrepreneurship and innovation.',
    }
  ];
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  
  return (
    <SocialSection>
      <SocialContainer>
        <SocialHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Connect on Social Media
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Follow Barkın Sayın on social media platforms for regular updates, insights, and to stay connected with his entrepreneurial journey.
          </PageSubtitle>
        </SocialHeader>
        
        <SocialGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {socialProfiles.map((profile, index) => (
            <SocialCard
              key={index}
              href={profile.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className={profile.icon}
            >
              <SocialIcon className={profile.icon}>
                {profile.icon === 'instagram' && (
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                )}
                {profile.icon === 'linkedin' && (
                  <svg viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                )}
                {profile.icon === 'youtube' && (
                  <svg viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                )}
              </SocialIcon>
              <SocialName>{profile.name}</SocialName>
              <SocialHandle>{profile.handle}</SocialHandle>
              <SocialDescription>{profile.description}</SocialDescription>
              <ConnectButton className={profile.icon}>
                {profile.icon === 'instagram' ? 'Follow' : profile.icon === 'linkedin' ? 'Connect' : 'Subscribe'}
              </ConnectButton>
            </SocialCard>
          ))}
        </SocialGrid>
      </SocialContainer>
    </SocialSection>
  );
};

export default Social; 